import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Box, Button, Stack, Typography } from "@mui/material";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router";
import { AccessButton } from "../../modules/program-access/components/access-button";
import {
  AddFolderButton,
  EditProgramButton,
} from "../../modules/program-edit/components/program-drawer";
import { ProgramFavouriteButton } from "../../modules/program-landing/components/program-favourite-button";
import { BAND, ProgramBadge, Tile } from "../../modules/program-landing/components/program-parts";
import { useProgramOverview } from "../../modules/program-landing/hooks/use-program-overview";
import { ProgramActionsMenu } from "../../modules/program-move/components/program-actions-menu";
import { ProgramTimeline } from "../../modules/program-timeline/components/program-timeline";
import { programHref } from "../../modules/program-tree/components/program-switcher";
import {
  SubProgramTabs,
  selectedFolder,
} from "../../modules/program-tree/components/sub-program-tabs";
import { levelText, programPath } from "../../modules/program-tree/program-tree";
import { uuidv4 } from "../../utils/uuid";

import type { ProgramNode, ProgramTree } from "../../modules/program-tree/program-tree";
import type { FC } from "react";

/**
 * The top of a program's page, on a band the width of the page: a link up to the program above it,
 * the program's icon, level and title with its star, its actions, the counts of the folder that the
 * tabs show, and the tabs. The counts include the folders below, and do not show until they load. A
 * new Communication goes into the program of the page, and only an editor of it can add one or add
 * a folder to it. Only an admin can change its access, change a top-level program, or move a
 * program; an editor can change a folder. Below the band, the timeline of the folder that the tabs
 * show.
 */
export const ProgramPage: FC<{
  tree: ProgramTree;
  current: ProgramNode;
  admin: boolean;
  /** After the program is changed, so that the programs can load again. */
  onProgramChanged: () => void;
}> = ({ tree, current, admin, onProgramChanged }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { data: overviews, reload: reloadOverviews } = useProgramOverview();
  const path = programPath(tree, current.id);
  const parent = path.at(-2);
  const focus = selectedFolder(current, searchParams.get("tab")) ?? current;
  const overview = overviews?.find(({ id }) => id === focus.id);
  const counts = overviews
    ? new Map(overviews.map(({ id, communications }) => [id, communications]))
    : undefined;

  return (
    <>
      <Box
        component="section"
        aria-label="Program overview"
        sx={{
          px: 4,
          pt: 4,
          borderBottom: 1,
          borderColor: "divider",
          background: (theme) =>
            theme.palette.mode === "dark"
              ? theme.palette.background.paper
              : `${BAND}, ${theme.palette.background.paper}`,
        }}
      >
        <Stack spacing={3} sx={{ maxWidth: 1376, mx: "auto" }}>
          {parent?.accessible ? (
            <Button
              component={RouterLink}
              to={programHref(parent.id)}
              size="small"
              color="inherit"
              startIcon={<ArrowBackIcon />}
              aria-label={`Back up to ${parent.title}`}
              sx={{ alignSelf: "flex-start", my: -1.5, ml: -1.25, color: "text.secondary" }}
              data-analytics-id="program-page-up"
            >
              {parent.title}
            </Button>
          ) : null}

          <Stack direction="row" useFlexGap sx={{ alignItems: "center", flexWrap: "wrap", gap: 3 }}>
            <ProgramBadge node={current} size={88} raised />
            <Stack spacing={0.5} sx={{ flex: 1, minWidth: 260 }}>
              <Typography
                variant="overline"
                component="p"
                sx={{ color: current.theme_colour, fontWeight: 600, lineHeight: "20px" }}
              >
                {levelText(path.length)}
              </Typography>
              <Stack direction="row" spacing={0.5} sx={{ alignItems: "center", minWidth: 0 }}>
                <Typography
                  variant="h4"
                  component="h1"
                  sx={{ minWidth: 0, overflowWrap: "anywhere" }}
                >
                  {current.title}
                </Typography>
                <ProgramFavouriteButton node={current} />
              </Stack>
            </Stack>
            <Stack direction="row" useFlexGap sx={{ flexWrap: "wrap", gap: 1 }}>
              {admin || (current.editable && current.parent_id !== null) ? (
                // Each program gets its own drawer, so a title is not saved on the wrong one.
                <EditProgramButton
                  key={current.id}
                  tree={tree}
                  program={current}
                  onSaved={onProgramChanged}
                />
              ) : null}
              {admin ? <AccessButton tree={tree} program={current} /> : null}
              {current.editable ? (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  component={RouterLink}
                  to={`/moments/${uuidv4()}?program_id=${current.id}`}
                  data-analytics-id="program-page-new-communication"
                >
                  New communication
                </Button>
              ) : null}
              {admin ? (
                <ProgramActionsMenu
                  key={`actions-${current.id}`}
                  tree={tree}
                  program={current}
                  onMoved={onProgramChanged}
                />
              ) : null}
            </Stack>
          </Stack>

          {overviews ? (
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 2 }}>
              <Tile label="Communications" value={overview?.communications ?? 0} />
              <Tile label="Active" value={overview?.active ?? 0} color="success.dark" />
              <Tile label="Drafts to finish" value={overview?.drafts ?? 0} />
            </Box>
          ) : null}

          <SubProgramTabs
            current={current}
            counts={counts}
            action={
              admin || current.editable ? (
                // The new folder's page shows once the programs load again.
                <AddFolderButton
                  key={current.id}
                  tree={tree}
                  parent={current}
                  onCreated={(id) => {
                    onProgramChanged();
                    void navigate(programHref(id));
                  }}
                />
              ) : null
            }
          />
        </Stack>
      </Box>
      <Box sx={{ px: 4, pt: 3, pb: 8 }}>
        <Box sx={{ maxWidth: 1376, mx: "auto" }}>
          <ProgramTimeline tree={tree} focus={focus} onChanged={reloadOverviews} />
        </Box>
      </Box>
    </>
  );
};
